import React, { useState } from "react";
import {
  FileText,
  FileCheck,
  AlertCircle,
  Calculator,
  Shield,
  Users,
  Zap,
  Award,
  Phone,
  Mail,
  Check,
  ChevronDown,
  MessageCircle,
  CheckCircle,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

import HowItWorks from "../components/HowItWorks";
import ContactBadge from "../components/ContactBadge";
import PaymentBadge from "../components/PaymentBadge";
import TrustBadge from "../components/TrustBadge";

const services = [
  {
    icon: FileCheck,
    title: "GST Registration",
    subtitle: "(Start Here)",
    description: "Business setup with legal verification",
    buttonText: "Start Now",
    gradient: "from-blue-600 to-blue-400",
    buttonColor: "bg-blue-600 hover:bg-blue-700",
  },
  {
    icon: FileText,
    title: "GST Return Filing",
    subtitle: "(Monthly / Quarterly)",
    description: "Timely filing to avoid penalties",
    buttonText: "File Returns",
    gradient: "from-emerald-600 to-emerald-400",
    buttonColor: "bg-emerald-600 hover:bg-emerald-700",
  },
  {
    icon: AlertCircle,
    title: "GST Notice & Assessment",
    subtitle: "",
    description: "Advocate-led reply & representation",
    buttonText: "Get Legal Help",
    gradient: "from-orange-500 to-orange-400",
    buttonColor: "bg-blue-800 hover:bg-blue-900",
  },
  {
    icon: Calculator,
    title: "Income Tax Return",
    subtitle: "(ITR)",
    description: "Tax planning + filing + scrutiny support",
    buttonText: "File ITR",
    gradient: "from-purple-600 to-purple-400",
    buttonColor: "bg-purple-600 hover:bg-purple-700",
  },
];

const reasons = [
  {
    icon: Shield,
    title: "Experienced Professionals",
    description:
      "Team of qualified tax & legal experts with strong industry experience.",
  },
  {
    icon: Zap,
    title: "Fast Processing",
    description: "Quick turnaround on GST, Income Tax, and compliance filings.",
  },
  {
    icon: Users,
    title: "24/7 Support",
    description: "Dedicated assistance under advocate supervision.",
  },
  {
    icon: Award,
    title: "100% Compliance",
    description: "Services aligned with latest tax laws and regulations.",
  },
];

const testimonials = [
  {
    name: "Rajesh Kumar",
    company: "Tech Solutions Ltd.",
    text: "TaxGST Pro has been instrumental in simplifying our tax compliance. Their team is professional and highly knowledgeable.",
    rating: 5,
  },
  {
    name: "Priya Sharma",
    company: "Fashion Retail Co.",
    text: "Excellent service and quick turnaround time. They helped us maximize our GST refunds significantly.",
    rating: 5,
  },
  {
    name: "Vikram Patel",
    company: "Manufacturing Inc.",
    text: "Outstanding support throughout our GST registration process. Highly recommended for any business.",
    rating: 5,
  },
];

const pricingPlans = [
  {
    name: "Starter",
    price: "₹999",
    period: "/month",
    description: "Perfect for small businesses and startups",
    features: [
      "GST Return Filing (GSTR-1 & 3B)",
      "Basic compliance support",
      "Email support",
      "Monthly reports",
      "1 GSTIN included",
    ],
    popular: false,
  },
  {
    name: "Professional",
    price: "₹2,499",
    period: "/month",
    description: "Ideal for growing businesses with complex needs",
    features: [
      "Everything in Starter",
      "GST Notice handling",
      "Income Tax Return filing",
      "Priority phone support",
      "Quarterly tax planning",
      "Up to 3 GSTINs",
      "Dedicated account manager",
    ],
    popular: true,
  },
  {
    name: "Enterprise",
    price: "₹4,999",
    period: "/month",
    description: "Complete solution for large enterprises",
    features: [
      "Everything in Professional",
      "Unlimited GSTINs",
      "Annual audit support",
      "Custom compliance reports",
      "On-site consultations",
      "24/7 priority support",
      "Tax optimization strategies",
      "Multi-state compliance",
    ],
    popular: false,
  },
];

const problem = [
  ["Income Tax Notice", "GST Demand", "Late Filing Penalty", "TDS Issues"],
  ["Audit & Scrutiny", "Tax Planning", "Audit & Scrutiny", "Tax Planning"],
];

  const trustBadges = [
    {
      title: "Advocate-Led Tax & Compliance Experts",
      subtitle: "10+ Years Experience",
    },
    { title: "Timely & Accurate Filing", subtitle: "99.9% Success Rate" },
    { title: "Online & In-Person Assistance", subtitle: "Flexible Service" },
  ];

  const trustIndicators = [
    "Confidential Handling",
    "Data Security",
    "Dedicated Support",
    "Professional Ethics",
  ];


const faqs = [
  {
    question: "What documents are required for GST registration?",
    answer:
      "For GST registration, you typically need PAN card of the business/owner, Aadhaar card, proof of business registration, bank account details, address proof of the business premises, and photographs of the authorized signatory. Our team will guide you through the complete documentation process.",
  },
  {
    question: "How long does GST registration take?",
    answer:
      "With proper documentation, GST registration typically takes 3-7 working days. Our express service can expedite this to 2-3 days in most cases. We handle the entire process from application to GSTIN generation.",
  },
  {
    question: "What are the penalties for late GST return filing?",
    answer:
      "Late filing of GST returns attracts a penalty of ₹50 per day for CGST and SGST each (₹100 total per day) for NIL returns, and ₹25 each (₹50 total) for other returns. Additionally, interest at 18% per annum is charged on the tax liability. We help you avoid these penalties with timely filing.",
  },
  {
    question: "Can you help with GST refund claims?",
    answer:
      "Yes, we provide complete assistance with GST refund claims including export refunds, inverted duty structure refunds, and excess payment refunds. Our team ensures proper documentation and follows up with authorities for quick processing.",
  },
  {
    question: "Do you provide support for GST audits?",
    answer:
      "Absolutely! Our Professional and Enterprise plans include comprehensive GST audit support. We help prepare reconciliation statements, resolve discrepancies, and represent your business during audit proceedings.",
  },
  {
    question: "What is included in Income Tax Return filing service?",
    answer:
      "Our ITR filing service includes computation of income from all sources, claiming eligible deductions and exemptions, preparation and e-filing of return, acknowledgment receipt, and assistance with any IT department communications.",
  },
];
const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        onClick={onClick}
      >
        <span className="font-semibold text-gray-900 pr-4">{question}</span>

        <ChevronDown
          className={`w-5 h-5 text-blue-600 flex-shrink-0 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <p className="px-6 pb-5 text-gray-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
};

const TaxService = () => {
const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <>
      {/* Hero Section */}
      <section
        className="relative py-20 md:py-32 px-4 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1554224155-6726b3ff858f')",
        }}
      >
        <div className="absolute inset-0 bg-blue-900/70"></div>
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center space-y-6 text-white">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Tax & Compliance Services
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Comprehensive tax and Compliance solutions for your business
            </p>
            <p className="text-blue-200 max-w-2xl mx-auto text-lg">
              Expert guidance in GST registration, return filing, compliance,
              and tax management.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <button className="bg-white text-blue-700 hover:bg-blue-100 px-8 py-3 rounded-lg font-semibold transition shadow-lg">
                Request Free Consultation
              </button>
              <button className="border-2 border-white text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition">
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* Trust Section */}
      <section className="relative w-full py-10 md:py-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        {/* Background with pattern */}
        <div className="absolute inset-0 bg-blue-50/50" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Decorative blurs */}
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-blue-400/20 rounded-full blur-3xl" />

        <div className="container relative max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-8 md:mb-12 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-4">
              <Sparkles className="w-4 h-4 text-blue-700" />
              <span className="text-sm font-semibold text-blue-700">
                Why Trust Us
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
              Your Trusted Tax Partner
            </h2>
          </div>

          {/* Top Row - Contact & Security */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-4 md:gap-6 mb-10 md:mb-12">
            <ContactBadge
              icon={MessageCircle}
              label="Questions? WhatsApp Us"
              value="+91 9473196196"
              href="https://wa.me/919473196196"
              delay={100}
            />
            <PaymentBadge delay={200} />
          </div>

          {/* Animated Divider */}
          <div className="relative w-full h-px mb-10 md:mb-12">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
            <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 rounded-full bg-blue-600 border-2 border-primary/30" />
          </div>

          {/* Trust Badges Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-10 md:mb-12">
            {trustBadges.map((badge, index) => (
              <TrustBadge
                key={badge.title}
                title={badge.title}
                subtitle={badge.subtitle}
                delay={300 + index * 100}
              />
            ))}
          </div>

          {/* Additional Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {trustIndicators.map((item, index) => (
              <div
                key={item}
                className="group flex items-center gap-2 px-4 py-2 bg-card/80 backdrop-blur-sm rounded-full border border-border/50 hover:border-primary/30 hover:bg-blue-200 transition-all duration-300 animate-fade-in cursor-default"
                style={{ animationDelay: `${600 + index * 50}ms` }}
              >
                <CheckCircle className="w-4 h-4 text-primary group-hover:scale-110 transition-transform duration-300" />
                <span className="text-sm font-medium text-foreground hover:text-black">
                  {item}
                </span>
              </div>
            ))}
          </div>

          {/* Stats Row */}
          <div className="mt-10 md:mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { value: "1000+", label: "Happy Clients" },
              { value: "15+", label: "Years Experience" },
              { value: "99.9%", label: "Success Rate" },
              { value: "24/7", label: "Support Available" },
            ].map((stat, index) => (
              <div
                key={stat.label}
                className="group text-center p-4 md:p-6 bg-card rounded-xl border border-border/50 shadow-soft hover:shadow-badge hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 animate-scale-in"
                style={{ animationDelay: `${700 + index * 100}ms` }}
              >
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 md:py-32 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 rounded-full mb-4">
              <span className="text-blue-700 font-semibold text-sm">
                What We Offer
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Tax & Compliance Journey
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From Registration to Representation — End-to-End Legal Support
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, idx) => {
              const Icon = service.icon;

              return (
                <div
                  key={idx}
                  className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition duration-300 bg-white border border-gray-200"
                >
                  {/* Top Gradient Header */}
                  <div
                    className={`bg-gradient-to-r ${service.gradient} p-6 text-white`}
                  >
                    <h3 className="text-xl font-bold">{service.title}</h3>
                    {service.subtitle && (
                      <p className="text-sm opacity-90 mt-1">
                        {service.subtitle}
                      </p>
                    )}
                  </div>

                  {/* Body */}
                  <div className="p-6 bg-gray-50">
                    <div className="w-14 h-14 bg-white shadow rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-gray-700" />
                    </div>

                    <p className="text-gray-600 mb-6">{service.description}</p>

                    <button
                      className={`w-full py-3 rounded-lg text-white font-semibold transition ${service.buttonColor}`}
                    >
                      {service.buttonText} →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Problem we solve */}
      <section className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-16 px-4">
        <div className="w-full max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-blue-100 rounded-full mb-4">
              <span className="text-blue-700 font-semibold text-sm">
                Problem We Solve
              </span>
            </div>
          {/* Heading */}
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Solving All Your Tax Needs
          </h2>

          {/* Services Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-6 sm:px-10 py-8 mb-8 shadow-sm border border-slate-100">
            {/* Flat grid - responsive columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 mb-8">
              {problem.flat().map((service, idx) => (
                <div key={idx} className="flex items-center gap-2 text-left">
                  <Check
                    className="w-4 h-4 text-emerald-500 flex-shrink-0"
                    strokeWidth={3}
                  />
                  <span className="text-gray-700 text-sm font-medium">
                    {service}
                  </span>
                </div>
              ))}
            </div>

            {/* Divider */}
            <div className="border-t border-slate-200 pt-6">
              <p className="text-gray-600 text-sm sm:text-base">
                We help{" "}
                <span className="text-blue-600 font-medium">
                  Individuals, Firms &amp; Companies
                </span>{" "}
                with Income Tax, GST &amp; TDS compliance.
              </p>
            </div>
          </div>

          <Link to="/contact-us">
            <button className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm sm:text-base py-3 px-8 rounded-full shadow-md hover:shadow-lg transition-all duration-200 group">
              Get Expert Tax Help
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
            </button>
          </Link>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        className="py-20 md:py-32 px-4 bg-gradient-to-b from-white to-blue-50"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Pricing Plans
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Choose the perfect plan for your business needs. All plans include
              expert support and compliance guarantee.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {pricingPlans.map((plan, idx) => (
              <div
                key={idx}
                className={`relative rounded-2xl p-8 transition-all duration-300 hover:scale-105 ${
                  plan.popular
                    ? "bg-gradient-to-b from-blue-600 to-blue-700 text-white shadow-2xl shadow-blue-500/30 md:-mt-4 md:mb-4"
                    : "bg-white border-2 border-gray-200 hover:border-blue-300 hover:shadow-xl"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3
                    className={`text-xl font-bold mb-2 ${
                      plan.popular ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {plan.name}
                  </h3>
                  <p
                    className={`text-sm mb-4 ${
                      plan.popular ? "text-blue-100" : "text-gray-500"
                    }`}
                  >
                    {plan.description}
                  </p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span
                      className={`text-4xl md:text-5xl font-bold ${
                        plan.popular ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {plan.price}
                    </span>
                    <span
                      className={
                        plan.popular ? "text-blue-200" : "text-gray-500"
                      }
                    >
                      {plan.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="flex items-start gap-3">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          plan.popular ? "bg-blue-500" : "bg-blue-100"
                        }`}
                      >
                        <Check
                          className={`w-3 h-3 ${
                            plan.popular ? "text-white" : "text-blue-600"
                          }`}
                        />
                      </div>
                      <span
                        className={
                          plan.popular ? "text-blue-50" : "text-gray-600"
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
                    plan.popular
                      ? "bg-white text-blue-600 hover:bg-blue-50 shadow-lg"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-500 mt-8">
            All prices are exclusive of GST. Annual billing available with 2
            months free.
          </p>
        </div>
      </section>

      <section id="how-it-works">
        <HowItWorks />
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-20 md:py-32 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Excellence in tax services backed by expertise and dedication
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reasons.map((reason, idx) => {
              const Icon = reason.icon;
              return (
                <div
                  key={idx}
                  className="p-6 bg-white rounded-xl border border-gray-200 text-center hover:shadow-lg transition"
                >
                  <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-32 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-gray-600 text-lg">
              Trusted by hundreds of businesses across India
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400">
                      ★
                    </span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  {testimonial.text}
                </p>
                <div>
                  <p className="font-semibold text-gray-900">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-gray-600">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 md:py-32 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              FAQ
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Get answers to common questions about our tax and GST services
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <FAQItem
                key={idx}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === idx}
                onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
              />
            ))}
          </div>

          <div className="mt-12 text-center p-8 bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl border border-blue-200">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Still have questions?
            </h3>
            <p className="text-gray-600 mb-4">
              Can't find the answer you're looking for? Our team is here to
              help.
            </p>
            <button className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold transition shadow-lg">
              Contact Support
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        id="contact"
        className="relative py-20 md:py-32 px-4 bg-gradient-to-b from-blue-600 to-blue-700 overflow-hidden"
      >
        <svg
          className="absolute top-0 left-0 w-full h-24 text-blue-500 opacity-20"
          preserveAspectRatio="none"
          viewBox="0 0 1200 120"
          fill="currentColor"
        >
          <path d="M0,50 Q300,0 600,50 T1200,50 L1200,0 L0,0 Z" />
          <path
            d="M50,60 Q350,20 650,60 T1250,60 L1250,0 L0,0 Z"
            opacity="0.5"
          />
          <path
            d="M100,70 Q400,30 700,70 T1300,70 L1300,0 L0,0 Z"
            opacity="0.3"
          />
        </svg>

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 text-balance leading-tight">
            Get Your Tax & Legal Compliance Done Professionally{" "}
          </h2>
          <p className="text-blue-100 text-lg md:text-xl mb-10 text-balance">
            Handled under Advocate Supervision with Complete Legal Safety{" "}
          </p>

          <button className="bg-white text-blue-600 hover:bg-gray-50 px-8 py-3 rounded-full font-semibold transition shadow-lg hover:shadow-xl inline-block">
            Book Free Consultation{" "}
          </button>

          <div className="mt-12 pt-8 border-t border-blue-500 border-opacity-50 flex justify-center gap-8 text-white flex-col sm:flex-row">
            <div className="flex items-center gap-3 justify-center">
              <Phone className="w-5 h-5 text-blue-200" />
              <a
                href="tel:+919473196196"
                className="text-blue-100 hover:text-white transition"
              >
                +91 9473196196
              </a>
            </div>

            <div className="flex items-center gap-3 justify-center">
              <Mail className="w-5 h-5 text-blue-200" />
              <a
                href="mailto:helpdesk@gha.org.in"
                className="text-blue-100 hover:text-white transition"
              >
                helpdesk@gha.org.in
              </a>
            </div>
          </div>
        </div>

        <svg
          className="absolute bottom-0 right-0 w-full h-24 text-blue-500 opacity-20 transform rotate-180"
          preserveAspectRatio="none"
          viewBox="0 0 1200 120"
          fill="currentColor"
        >
          <path d="M0,50 Q300,0 600,50 T1200,50 L1200,120 L0,120 Z" />
          <path
            d="M50,60 Q350,20 650,60 T1250,60 L1250,120 L0,120 Z"
            opacity="0.5"
          />
          <path
            d="M100,70 Q400,30 700,70 T1300,70 L1300,120 L0,120 Z"
            opacity="0.3"
          />
        </svg>
      </section>
    </>
  );
};

export default TaxService;
